<template>
  <p-nav id="navbar" :class="navbarClasses" :style="navbarStyles" :new-design="isNewDesign">
    <!-- Logo -->
    <div @click="() => navigationItemClicked(rootUrl)">
      <p-nav-logo :href="rootUrl" class="xl:!mr-8 !mr-4">
        <span class="sr-only">remove.bg</span>
        <p-logo-rbg />
      </p-nav-logo>
    </div>

    <!-- Desktop navigation -->
    <ul class="hidden xl:flex flex-grow items-center gap-1 xl:gap-1 my-0">
      <li>
        <Button variant="ghost" as="link" :href="newImageUrl" @click.native="() => navigationItemClicked(newImageUrl)">
          {{ I18n.t("navigation.uploads") }}
        </Button>
      </li>
      <template>
        <li>
          <Button
            variant="ghost"
            as="link"
            :href="bulkEditingUrl"
            @click.native="() => navigationItemClicked(bulkEditingUrl)"
          >
            {{ I18n.t("navigation.bulk_editing") }}
          </Button>
        </li>
        <li>
          <Button variant="ghost" as="link" :href="apiUrl" @click.native="() => navigationItemClicked(apiUrl)">
            {{ I18n.t("navigation.api") }}
          </Button>
        </li>
        <li>
          <Button variant="ghost" as="link" :href="toolsUrl" @click.native="() => navigationItemClicked(toolsUrl)">
            {{ I18n.t("navigation.plugins") }}
          </Button>
        </li>
      </template>
      <li>
        <Button variant="ghost" as="link" :href="pricingUrl" @click.native="() => navigationItemClicked(pricingUrl)">
          {{ I18n.t("navigation.pricing") }}
          <p-badge v-if="blackFriday" class="ml-1" tone="primary">-40%</p-badge>
        </Button>
      </li>
    </ul>

    <!-- Account dropdown -->
    <ul class="hidden flex-none xl:flex items-center gap-2 xl:gap-2 my-0">
      <li v-if="!signedIn">
        <Button variant="ghost" as="link" :href="loginUrl">
          {{ I18n.t("navigation.login") }}
        </Button>
      </li>
      <li v-if="!signedIn">
        <p-button variant="secondary" :href="signupUrl" as="link">
          {{ I18n.t("navigation.signup") }}
        </p-button>
      </li>
      <li v-if="signedIn">
        <p-dropdown align-right :on-open="() => accountDropdownOpened()" vertical-offset="16">
          <template #trigger>
            <p-avatar>{{ initals }}</p-avatar>
          </template>
          <template #content>
            <div class="!p-6 flex flex-col gap-y-4" style="width: 260px">
              <span class="text-typo-secondary font-bold">{{ displayName }}</span>
              <div class="flex flex-col gap-y-1">
                <span class="text-brand-typo font-bold text-2xl text-break">
                  {{ I18n.t("dashboard.credits", { count: credits }) }}
                </span>
                <p-link :href="pricingUrl" hide-arrow>{{ I18n.t("dashboard.buy_credits_or_subscribe") }}</p-link>
              </div>
              <p-nav-link height="sm" :href="accountUrl">{{ I18n.t("navigation.my_account") }}</p-nav-link>
              <p-nav-link height="sm" :href="logoutUrl" data-method="delete">{{
                I18n.t("navigation.logout")
              }}</p-nav-link>
            </div>
          </template>
        </p-dropdown>
      </li>
    </ul>

    <!-- Mobile -->
    <div class="flex flex-none xl:hidden items-center">
      <a v-if="blackFriday" :href="pricingUrl" class="no-underline">
        <p-badge class="ml-1 sm:block" tone="primary">-40%</p-badge>
      </a>
      <p-burger-menu :on-open="mobileOpenCallback" :on-close="mobileCloseCallback">
        <template #content>
          <ul class="flex flex-col divide-y divide-secondary">
            <li v-if="signedIn">
              <a :href="accountUrl" class="flex space-x-4 py-8 text-typo no-underline">
                <p-avatar class="flex-shrink-0">{{ initals }}</p-avatar>
                <span class="flex flex-col divide-none overflow-hidden justify-center">
                  <span v-if="displayName != email" class="font-bold">{{ displayName }}</span>
                  <span class="truncate flex-shrink-1">{{ email }}</span>
                </span>
              </a>
            </li>

            <li v-if="signedIn">
              <div class="py-8">
                <span class="text-brand-typo font-bold text-2xl">
                  {{ I18n.t("dashboard.credits", { count: credits }) }}
                </span>
                <p-link :href="pricingUrl" hide-arrow @click.native="() => navigationItemClicked(pricingUrl)">{{
                  I18n.t("dashboard.buy_credits_or_subscribe")
                }}</p-link>
              </div>
            </li>

            <li>
              <p-nav-link :href="newImageUrl" @click.native="() => navigationItemClicked(newImageUrl)" height="lg">
                {{ I18n.t("navigation.uploads") }}
              </p-nav-link>
            </li>
            <template>
              <li>
                <p-nav-link
                  height="lg"
                  :href="bulkEditingUrl"
                  @click.native="() => navigationItemClicked(bulkEditingUrl)"
                >
                  {{ I18n.t("navigation.bulk_editing") }}
                </p-nav-link>
              </li>
              <li>
                <p-nav-link height="lg" :href="apiUrl" @click.native="() => navigationItemClicked(apiUrl)">
                  {{ I18n.t("navigation.api") }}
                </p-nav-link>
              </li>
              <li>
                <p-nav-link height="lg" :href="toolsUrl" @click.native="() => navigationItemClicked(toolsUrl)">
                  {{ I18n.t("navigation.integrations") }}
                </p-nav-link>
              </li>
            </template>

            <li>
              <p-nav-link height="lg" :href="pricingUrl" @click.native="() => navigationItemClicked(pricingUrl)">
                {{ I18n.t("navigation.pricing") }}
                <p-badge v-if="blackFriday" class="ml-1" tone="primary">-40%</p-badge>
              </p-nav-link>
            </li>

            <li v-if="!signedIn">
              <p-nav-link height="lg" :href="loginUrl">
                {{ I18n.t("navigation.login") }}
              </p-nav-link>
            </li>
            <li v-if="signedIn">
              <p-nav-link height="lg" data-method="delete" :href="logoutUrl">
                {{ I18n.t("navigation.logout") }}
              </p-nav-link>
            </li>
          </ul>
          <ul v-if="!signedIn" class="flex flex-col divide-y divide-secondary">
            <li class="w-full">
              <p-button
                ref="signupButton"
                v-if="!signedIn"
                as="link"
                :full-width="true"
                variant="secondary"
                :href="signupUrl"
                @focus.native="scrollToView"
                >{{ I18n.t("navigation.signup") }}
              </p-button>
            </li>
          </ul>
        </template>
      </p-burger-menu>
    </div>
  </p-nav>
</template>

<script setup>
import "@/src/i18n";
import { ref, inject, computed, onMounted, onUnmounted, nextTick } from "vue";
import emitter from "@/modules/event_bus";
import { Button } from "prism";

import { useCredits } from "@/composables/credits";
import { rbgMainNavigationClickedV100, rbgMainNavigationDropdownOpenedV100 } from "kaleido-event-tracker";

import { usePhotoEditor } from "@/composables/photo_editor";

const mobileNavOpen = ref(false);
const { credits, isLoading, fetchCredits } = useCredits();
const I18n = inject("I18n");

const { isNewEditorEnabled } = usePhotoEditor();

// Reactive reference to track current pathname
const currentPathname = ref(window.location.pathname);

const props = defineProps({
  signedIn: Boolean,
  blackFriday: Boolean,
  initals: String,
  displayName: String,
  email: String,
  rootUrl: String,
  newImageUrl: String,
  creditsInfoUrl: String,
  pricingUrl: String,
  signupUrl: String,
  loginUrl: String,
  logoutUrl: String,
  accountUrl: String,
  toolsUrl: String,
  contactSalesUrl: String,
  resourcesUrl: String,
  helpCenterUrl: String,
  bulkEditingUrl: String,
  apiUrl: String,
});

const mobileOpenCallback = () => {
  mobileNavOpen.value = true;
  document.body.classList.add("lock-scroll");
  fetchCredits();
};

const mobileCloseCallback = () => {
  document.body.classList.remove("lock-scroll");
  mobileNavOpen.value = false;
};

const isNewDesign = computed(() => {
  return isNewEditorEnabled.value && currentPathname.value.includes("/upload");
});

const isUploadPage = computed(() => {
  return currentPathname.value.includes("/upload");
});

const navbarClasses = computed(() => {
  return {
    "d-none-mobile-app": true,
    "hidden lg:block": isUploadPage.value,
  };
});

const signupButton = ref(null);

emitter.on("purchase", () => {
  isLoading.value = false;
});

const updateCurrentPathname = () => {
  currentPathname.value = window.location.pathname;
};

const handlePopState = () => {
  updateCurrentPathname();
};

onMounted(() => {
  const maintenanceModeBanner = document.querySelector("#maintenanceModeBanner");
  maintenanceModeBannerVisible.value = !!maintenanceModeBanner;

  // Explanation: This hack is used to update the current pathname. Based on that we are applying different styles to the navbar.
  // Since we don't have any client side router, we are using the history API to update the current pathname.
  window.addEventListener("popstate", handlePopState);

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(history, args);
    updateCurrentPathname();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(history, args);
    updateCurrentPathname();
  };
});

onUnmounted(() => {
  window.removeEventListener("popstate", handlePopState);
});

const scrollToView = () => {
  if (signupButton.value && signupButton.value.$el) {
    signupButton.value.$el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const maintenanceModeBannerVisible = ref(false);
emitter.on("maintenanceModeBannerVisibilityChanged", (visible) => {
  maintenanceModeBannerVisible.value = visible;

  const toolsNav = document.querySelector("#tools-nav");
  if (!toolsNav) return;

  if (visible) {
    toolsNav.setAttribute("style", `padding-top: 1rem !important`);
  } else if (toolsNav) {
    toolsNav.setAttribute("style", `padding-top: 1rem !important`);
  }
});

const navbarStyles = computed(() => {
  const maintenanceModeBanner = document.querySelector("#maintenanceModeBanner");

  if (maintenanceModeBanner) {
    nextTick(() => {
      const pageContent = document.getElementById("page-content");
      pageContent.style.marginTop = `${maintenanceModeBanner.clientHeight + 72}px`;
    });
    return {
      top: maintenanceModeBannerVisible.value ? maintenanceModeBanner.clientHeight + "px" : "0",
    };
  }
});

const navigationItemClicked = (url) => {
  rbgMainNavigationClickedV100({
    url_path: url,
  });
};

const navigationDropdownOpened = (dropdown_name) => {
  rbgMainNavigationDropdownOpenedV100({
    dropdown_name,
  });
};

const accountDropdownOpened = async () => {
  navigationDropdownOpened("account");
  fetchCredits();
};
</script>

<style>
.lock-scroll {
  overflow: hidden;
}
nav {
  transition: top 0.5s;
  transition: padding-top 0.5s;
  ul {
    button {
      border-radius: 999px;
      &:focus {
        outline: none !important;
      }
      &:focus-visible {
        outline: 2px solid #0f70e6 !important;
        outline-offset: 2px !important;
      }
    }
  }
}
</style>
